import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Marquee from 'react-fast-marquee';
import styles from './LatestWins.module.css';

/**
 * Latest Wins Component
 * Displays recent big wins in a marquee-style scrolling display
 */

// Crypto config for currency icons and formatting (using coin icons from /assets/images/coins/)
const cryptoConfig = {
  USDT: {
    icon: '/assets/images/coins/USDT.webp',
    symbol: '$',
    minBet: 50000,
    maxBet: 500000,
    decimals: 0
  },
  INR: {
    icon: '/assets/images/coins/INR.webp',
    symbol: 'INR',
    minBet: 5000,
    maxBet: 50000,
    decimals: 0
  },
  PKR: {
    icon: '/assets/images/coins/INR.webp', // Fallback to INR if PKR not available
    symbol: '$',
    minBet: 5000,
    maxBet: 50000,
    decimals: 0
  },
  BTC: {
    icon: '/assets/images/coins/BTC.webp',
    symbol: 'BTC',
    minBet: 0.001,
    maxBet: 0.1,
    decimals: 8
  },
};

// Generate unique usernames (like old code)
const generateUsernames = (count) => {
  const nameParts = [
    "Crypto", "Bitcoin", "Satoshi", "Hodl", "Moon", "Rocket", "Diamond", "Whale", "Bull", "Bear", "Degen", "Ape", "Chain", "Block", "Hash",
    "Simon", "Alex", "Max", "Elon", "Vital", "Sam", "Mike", "John", "Lisa", "Emma", "Sophia", "Jack", "Ryan", "Gia", "Leo", "Zack",
    "Player", "Gamer", "Winner", "Lucky", "Pro", "Elite", "Master", "Champ", "Star", "King", "Queen", "Ace", "Hero", "Legend", "Boss"
  ];

  const nameSuffixes = [
    "TheMoon", "Hodler", "Trader", "Winner", "Pro", "Master", "King", "Queen", "Guru", "Wizard", "Ninja", "Whale", "Shark", "Hunter", "Boss",
    "123", "007", "2024", "99", "88", "777", "666", "555", "444", "333", "22", "11", "00", "X", "Z", "Y", "Alpha", "Beta", "Omega", "Prime"
  ];

  const usedUsernames = new Set();
  const usernames = [];

  for (let i = 0; i < count; i++) {
    let username = "";
    let attempts = 0;

    do {
      const pattern = Math.floor(Math.random() * 4);
      switch (pattern) {
        case 0:
          username = `${nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 4)}${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
          break;
        case 1:
          username = `${nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 5)}${Math.floor(Math.random() * 10)}`;
          break;
        case 2:
          const name1 = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 3);
          const name2 = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 3);
          username = `${name1}${name2}`;
          break;
        case 3:
          username = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 6);
          break;
      }
      attempts++;
      if (attempts > 100) {
        username = `User${Math.floor(Math.random() * 100)}`;
        break;
      }
    } while (usedUsernames.has(username) || username.length > 6);

    usedUsernames.add(username);
    usernames.push(username);
  }

  return usernames;
};

// Generate random profit (like old code)
const generateRandomProfit = (min, max, decimals) => {
  const factor = 10 ** decimals;
  return ((Math.random() * (max - min) + min) * factor).toFixed(decimals) / factor;
};

// Mask player ID for display (e.g. "30***56")
const maskPlayerId = (username) => {
  if (!username || typeof username !== 'string') return '*******';
  const s = String(username).replace(/\s/g, '');
  if (s.length < 4) return s.slice(0, 1) + '***' + (s.slice(-1) || '*');
  return s.slice(0, 2) + '***' + s.slice(-2);
};

// Format profit amount (matching old code exactly)
const formatProfit = (amount, currency) => {
  const config = cryptoConfig[currency] || cryptoConfig.USDT;
  const { symbol } = config;
  const numAmount = Number(amount) || 0;

  if (currency === 'BTC') {
    // For BTC, show 4 decimal places max to keep it short
    return `${symbol}${Number(numAmount).toFixed(4)}`;
  }

  if (numAmount >= 100000) {
    // Convert to M for millions
    return `${symbol}${(numAmount / 1000000).toFixed(1)}M`;
  }

  if (numAmount >= 1000) {
    // Convert to K for thousands
    return `${symbol}${(numAmount / 1000).toFixed(1)}K`;
  }

  // For small amounts, just show the number
  return `${symbol}${Math.round(numAmount)}`;
};

const LatestWins = ({ all = [], slots = [], liveCasino = [], providers = null, isLoading: propsLoading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState(new Set());

  // Combine all props data (like old code: all={[...jsPragmeticSlots]} slots={jsPragmeticSlots} liveCasino={jsPragmeticSlots})
  // This uses the API fetched data from jsPragmeticSlots
  const combinedPropsData = useMemo(() => {
    // Merge all arrays and remove duplicates
    const merged = [...all, ...slots, ...liveCasino];
    let unique = merged.filter((item, index, self) => {
      const itemId = item.id || item.uuid || item.game_uid;
      const firstIndex = self.findIndex((t) => (t.id || t.uuid || t.game_uid) === itemId);
      return index === firstIndex;
    });
    
    // Filter by providers if provided
    if (providers && Array.isArray(providers) && providers.length > 0) {
      // Map user-provided provider names to exact API provider names
      const providerNameMap = {
        'Betsoft': ['Betsoft', 'betsoft', 'Bet Soft', 'bet soft'],
        'Igrosoft': ['Igrosoft', 'igrosoft', 'Igro Soft', 'igro soft'],
        'OneTouch': ['OneTouch', 'onetouch', 'One Touch', 'one touch', '1Touch', '1touch'],
        'BFGames': ['BFGames', 'bfgames', 'BF Games', 'bf games'],
        'BoomingGames': ['BoomingGames', 'boominggames', 'Booming Games', 'booming games'],
        // 'PragmaticPlay': ['PragmaticPlay', 'pragmaticplay', 'Pragmatic Play', 'pragmatic play', 'Pragmatic', 'pragmatic'],
      };
      
      // Create a set of all possible provider name variations to match
      const providerVariations = new Set();
      providers.forEach(provider => {
        const normalized = provider.trim();
        // Add the exact name
        providerVariations.add(normalized);
        // Add normalized versions (lowercase, no spaces)
        providerVariations.add(normalized.toLowerCase().replace(/\s+/g, ''));
        // Add mapped variations if they exist
        if (providerNameMap[normalized]) {
          providerNameMap[normalized].forEach(variant => {
            providerVariations.add(variant);
            providerVariations.add(variant.toLowerCase().replace(/\s+/g, ''));
          });
        }
      });
      
      const beforeFilterCount = unique.length;
      unique = unique.filter((item) => {
        const gameProvider = (item.provider || item.vendor || '').trim();
        const gameProviderNormalized = gameProvider.toLowerCase().replace(/\s+/g, '');
        
        // Check if game provider matches any variation
        const matches = providerVariations.has(gameProvider) || 
                       providerVariations.has(gameProviderNormalized) ||
                       Array.from(providerVariations).some(variant => {
                         const variantNormalized = typeof variant === 'string' ? variant.toLowerCase().replace(/\s+/g, '') : '';
                         return gameProviderNormalized === variantNormalized || 
                                gameProviderNormalized.includes(variantNormalized) ||
                                variantNormalized.includes(gameProviderNormalized);
                       });
        return matches;
      });
      
    }
    
    return unique;
  }, [all, slots, liveCasino, providers]);

  // Use React Query for data fetching (fallback if no props data)
  // Note: API endpoint not available, so disabled
  const { data, isLoading } = useQuery({
    queryKey: ['latestWins'],
    queryFn: async () => {
      // API endpoint /api/latest-wins does not exist
      return [];
    },
    enabled: false, // Disabled - API endpoint not available
    staleTime: 30000,
    refetchInterval: 30000,
    retry: 1,
  });

  // Process and map the wins data properly (like old code pattern)
  // Priority: Use props data if available, otherwise use API data
  const processedWins = useMemo(() => {
    // If we have props data, use it (like old code: all={[...jsPragmeticSlots]} slots={jsPragmeticSlots} liveCasino={jsPragmeticSlots})
    if (combinedPropsData.length > 0) {
      // Generate unique usernames for all games (like old code)
      const uniqueUsernames = generateUsernames(combinedPropsData.length);
      
      return combinedPropsData.map((game, index) => {
        // Map game data to win structure (similar to old code pattern)
        // Data structure from API: { id, uuid, game_uid, name, game_name, game_icon, image, game_type, provider, source }
        const currencies = ['USDT', 'INR', 'BTC'];
        const currency = game.currency || game.currency_code || currencies[Math.floor(Math.random() * currencies.length)];
        const currencyConfig = cryptoConfig[currency] || cryptoConfig.USDT;
        const { minBet, maxBet, decimals } = currencyConfig;
        
        // Generate random profit like old code
        const profitAmount = game.profit || game.amount || game.win_amount || generateRandomProfit(minBet, maxBet, decimals);
        const formattedProfit = formatProfit(profitAmount, currency);

        // Validate and clean image URL
        const gameIcon = game.game_icon || game.image || '';
        const validImageUrl = gameIcon && 
                             gameIcon !== '/assets/images/placeholder.webp' &&
                             gameIcon !== 'undefined' &&
                             gameIcon !== 'null' &&
                             gameIcon.trim() !== '' &&
                             (gameIcon.startsWith('http') || gameIcon.startsWith('/'));

        return {
          id: game.id || game.uuid || game.game_uid || `game-${index + 1}`,
          username: game.username || game.player_name || game.player || uniqueUsernames[index],
          game_name: game.name || game.game_name || 'Game',
          game_icon: validImageUrl ? gameIcon : null, // Set to null if invalid, will be filtered out
          profit: formattedProfit,
          currency: currency,
          currencyIcon: currencyConfig.icon,
        };
      });
    }

    // Fallback to API data (if available in future)
    // Handle undefined data from disabled query
    const apiData = data || [];
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    return apiData.map((win, index) => {
      // Map API response fields to component structure
      const currency = win.currency || win.currency_code || 'USDT';
      const currencyConfig = cryptoConfig[currency] || cryptoConfig.USDT;
      const profitAmount = win.profit || win.amount || win.win_amount || 0;
      const formattedProfit = formatProfit(profitAmount, currency);

      // Validate and clean image URL
      const winIcon = win.game_icon || win.image || win.game_image || '';
      const validWinImageUrl = winIcon && 
                              winIcon !== '/assets/images/placeholder.webp' &&
                              winIcon !== 'undefined' &&
                              winIcon !== 'null' &&
                              winIcon.trim() !== '' &&
                              (winIcon.startsWith('http') || winIcon.startsWith('/'));

      return {
        id: win.id || win.bet_id || index + 1,
        username: win.username || win.player_name || win.player || `Player${index + 1}`,
        game_name: win.game_name || win.game || 'Game',
        game_icon: validWinImageUrl ? winIcon : null, // Set to null if invalid, will be filtered out
        profit: formattedProfit,
        currency: currency,
        currencyIcon: currencyConfig.icon,
      };
    });
  }, [combinedPropsData, data]);
  

  // Filter out wins with failed images or invalid image URLs
  const validWins = useMemo(() => {
    return processedWins.filter((win) => {
      // Skip items where image has failed to load
      if (failedImages.has(win.id)) {
        return false;
      }
      // Skip items with invalid or missing image URLs
      if (!win.game_icon ||
          win.game_icon === null ||
          win.game_icon === '/assets/images/placeholder.webp' ||
          win.game_icon.trim() === '' ||
          win.game_icon === 'undefined' ||
          win.game_icon === 'null') {
        return false;
      }
      // Skip games with names longer than 12 characters
      if (win.game_name && win.game_name.length > 12) {
        return false;
      }
      return true;
    });
  }, [processedWins, failedImages]);

  // Handle image load errors
  const handleImageError = (winId, event) => {
    // Prevent infinite loop by checking if we've already marked this image as failed
    if (!failedImages.has(winId)) {
      setFailedImages((prev) => new Set([...prev, winId]));
      // Set placeholder to prevent broken image icon
      if (event.target) {
        event.target.src = '/assets/images/placeholder.webp';
        // Hide the image if placeholder also fails
        event.target.onerror = () => {
          event.target.style.display = 'none';
        };
      }
    }
  };

  // Show loading while props are loading OR while we have no data yet (gives retries time)
  const [initialGracePeriod, setInitialGracePeriod] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setInitialGracePeriod(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  const showLoading = (propsLoading && combinedPropsData.length === 0) ||
                      (isLoading && combinedPropsData.length === 0 && processedWins.length === 0) ||
                      (validWins.length === 0 && initialGracePeriod);

  const badgeIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.badgeIcon} aria-hidden>
      <path fillRule="evenodd" clipRule="evenodd" d="M12 16a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm0-10c-.284 0-.474.34-.854 1.023l-.098.176c-.108.194-.162.29-.246.354-.085.064-.19.088-.4.135l-.19.044c-.738.167-1.107.25-1.195.532-.088.282.164.577.667 1.165l.13.152c.143.167.215.25.247.354.032.104.021.215 0 .438l-.02.203c-.076.785-.114 1.178.115 1.352.23.174.576.015 1.267-.303l.178-.082c.197-.09.295-.135.399-.135.104 0 .202.045.399.135l.178.082c.691.319 1.037.477 1.267.303.23-.174.191-.567.115-1.352l-.02-.203c-.021-.223-.032-.334 0-.438.032-.104.104-.187.247-.354l.13-.152c.503-.588.755-.882.667-1.165-.088-.282-.457-.365-1.195-.532l-.19-.044c-.21-.047-.315-.07-.4-.135-.084-.064-.138-.16-.246-.354l-.098-.176C12.474 6.34 12.284 6 12 6Z" fill="currentColor" />
      <path d="m4.495 12.993-1.503 1.555c-.54.56-.81.84-.904 1.076-.213.54-.03 1.138.433 1.422.204.124.57.163 1.305.24.414.044.622.066.795.133.39.15.69.462.835.864.064.18.085.394.127.823.075.76.113 1.14.233 1.351.274.48.853.67 1.374.448.228-.096.498-.376 1.04-.935l2.481-2.57a8.501 8.501 0 0 1-6.216-4.407Zm8.795 4.408 2.482 2.57c.54.56.81.84 1.038.936.521.22 1.1.031 1.374-.449.12-.21.157-.59.232-1.35.043-.43.064-.644.128-.824.144-.402.446-.715.835-.864.173-.067.38-.088.795-.132.734-.078 1.101-.117 1.305-.24.463-.285.646-.884.433-1.423-.094-.237-.364-.517-.904-1.076l-1.503-1.556a8.5 8.5 0 0 1-6.216 4.408" fill="currentColor" />
    </svg>
  );

  if (showLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.sliderContainer}>
          <div className={styles.sliderBadge}>
            {badgeIcon}
            <span className={styles.sliderBadgeTitle}>{t('home.recent')}<br />{t('home.wins')}</span>
          </div>
          <div className={styles.skeletonContainer}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard}>
                <div className={styles.skeletonImage} style={{ animationDelay: `${i * 0.1}s` }} />
                <div className={styles.skeletonContent}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (validWins.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.sliderContainer}>
          <div className={styles.sliderBadge}>
            {badgeIcon}
            <span className={styles.sliderBadgeTitle}>{t('home.recent')}<br />{t('home.wins')}</span>
          </div>
          <div className={styles.emptyState}>
            <p>{t('home.noActivityYet')}</p>
            <button
              className={styles.emptyStateCta}
              onClick={() => navigate('/casino')}
            >
              {t('home.exploreGames')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderBadge}>
          {badgeIcon}
          <span className={styles.sliderBadgeTitle}>{t('home.recent')}<br />{t('home.wins')}</span>
        </div>
        <div className={styles.marqueeContainer}>
          <Marquee
            speed={40}
            gradient={false}
            pauseOnHover={true}
            autoFill={true}
            direction="left"
          >
            {validWins.map((win) => (
              <div key={win.id} className={styles.winCard}>
                <div className={styles.gameImageContainer}>
                  <img
                    src={win.game_icon}
                    alt={win.game_name}
                    className={styles.gameImage}
                    loading="lazy"
                    onError={(e) => handleImageError(win.id, e)}
                  />
                </div>
                <div className={styles.winContent}>
                  <span className={styles.gameName}>{win.game_name}</span>
                  <span className={styles.playerId}>{maskPlayerId(win.username)}</span>
                  <span className={styles.profitAmount}>{win.profit}</span>
                </div>
              </div>
            ))}
          </Marquee>
        </div>
      </div>
    </div>
  );
};

export default LatestWins;
